/**
 * Which recording hosts a browser can be handed a URL for, and on whose terms.
 *
 * <p>There are TWO different credentials in play, and they are not
 * interchangeable:
 *
 * <ul>
 *   <li>the `recording-tvoice*` distributions check the SESSION token — the
 *       same JWT the portal logs in with, passed as `?token=`;</li>
 *   <li>`recordings.greengiraffe.in` and `recordings.xtremegenai.com` check a
 *       SHORT-LIVED token cpass mints per call, passed as `?t=`, whose claims
 *       say which bucket prefixes the caller may read. The session JWT means
 *       nothing to them — their CloudFront function answers 401 `missing
 *       token`, because `token` is not `t`.</li>
 * </ul>
 *
 * <p>Both used to sit in one list called "hosts the session token works on",
 * which is how a stored `recordings.greengiraffe.in` URL got the session token
 * appended and short-circuited the resolver: the CDN was tried first, 401'd,
 * and the cpass path that mints the token the CDN actually wants was never
 * reached.
 *
 * <p>What is in NEITHER list is the SWITCH. On the Green Giraffe estate
 * `recording_url` points straight at the recording host behind HTTP Basic, and
 * a browser can never play that: it has no credential to offer and the session
 * JWT is refused. A stored URL that is on no list is a LAST resort, tried only
 * after cpass has been asked.
 *
 * <p>Kept out of recordings.service.ts because that module imports axios, which
 * drags a network client into any test that wants this rule.
 */

/**
 * Distributions whose auth function accepts the portal's own session JWT.
 *
 * Verified against the live distributions, 2026-09-08. E52M7CH8IYN9W serves
 * the first three (origin ivr-recording-eu); EBY23B6M12LHC serves the -af one
 * (origin ivr-recording-af-1). The bare `recording-tvoice` host is NOT the one
 * Africa's call rows actually carry — those are `-af` — so listing only it
 * meant this returned false for every real Africa recording and the CDN was
 * never tried first. Confirmed playable: token appended -> HTTP 200,
 * audio/x-wav.
 */
const SESSION_TOKEN_HOSTS = [
  'recording-tvoice.teeoff.tech',
  'recordings-tvoice.teeoff.tech',
  'recording-tvoice-af.teeoff.tech',
  'recording-tvoice-eu.teeoff.tech',
];

/**
 * Distributions that require the token cpass mints — `/calls/{id}/recording`
 * returns a complete URL for these, already signed. Nothing the browser holds
 * opens them, so they must never be reached by appending the session token.
 *
 * E7KQIE07UAUUS serves recordings.greengiraffe.in from voicex-recordings-mi,
 * with the `recordings-greengiraffe-auth` viewer-request function in front.
 */
const SIGNED_TOKEN_HOSTS = [
  'recordings.greengiraffe.in',
  'recordings.xtremegenai.com',
];

function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://localhost';
    return new URL(url, base).hostname;
  } catch {
    return null;
  }
}

/**
 * Whether appending the SESSION token to this URL produces something playable.
 *
 * Matches on HOST — a substring check would send the session token elsewhere.
 */
export function isCdnRecordingUrl(url: string | null | undefined): boolean {
  const h = hostOf(url);
  return h !== null && SESSION_TOKEN_HOSTS.includes(h);
}

/** Whether this host serves only URLs cpass has signed. */
export function isSignedTokenRecordingUrl(url: string | null | undefined): boolean {
  const h = hostOf(url);
  return h !== null && SIGNED_TOKEN_HOSTS.includes(h);
}
